import { useEffect, useRef, useCallback } from 'react';
import { clamp } from '../utils';
import { useUI } from '../contexts';

type DragTarget = 'sidebar' | 'preview' | null;
type DragAnchor = 'left' | 'right';

type DragState = {
  target: DragTarget;
  anchor: DragAnchor;
  startX: number;
  startSidebar: number;
  startPreview: number;
};

export function useResize() {
  const {
    sidebarWidth,
    previewWidth,
    setSidebarWidth,
    setPreviewWidth,
  } = useUI();
  const dragState = useRef<DragState>({
    target: null,
    anchor: 'right',
    startX: 0,
    startSidebar: sidebarWidth,
    startPreview: previewWidth,
  });
  const resizeRaf = useRef<number | null>(null);

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!dragState.current.target) return;
      if (resizeRaf.current) cancelAnimationFrame(resizeRaf.current);
      resizeRaf.current = requestAnimationFrame(() => {
        const dx = e.clientX - dragState.current.startX;
        const direction = dragState.current.anchor === 'right' ? 1 : -1;
        if (dragState.current.target === 'sidebar') {
          const next = clamp(dragState.current.startSidebar + dx * direction, 200, 520);
          setSidebarWidth(next);
        } else if (dragState.current.target === 'preview') {
          const next = clamp(dragState.current.startPreview + dx * direction, 280, 560);
          setPreviewWidth(next);
        }
      });
    };

    const handleUp = () => {
      if (dragState.current.target) {
        dragState.current = {
          target: null,
          anchor: 'right',
          startX: 0,
          startSidebar: sidebarWidth,
          startPreview: previewWidth,
        };
        document.body.style.cursor = '';
        if (resizeRaf.current) {
          cancelAnimationFrame(resizeRaf.current);
          resizeRaf.current = null;
        }
      }
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [previewWidth, sidebarWidth, setSidebarWidth, setPreviewWidth]);

  const startResize = useCallback(
    (target: DragTarget, anchor: DragAnchor, startX: number) => {
      dragState.current = {
        target,
        anchor,
        startX,
        startSidebar: sidebarWidth,
        startPreview: previewWidth,
      };
      document.body.style.cursor = 'col-resize';
    },
    [sidebarWidth, previewWidth]
  );

  return { startResize, dragState, resizeRaf };
}
